// services/firestoreProducts.js
import {
    collection,
    addDoc,
    Timestamp,
    query,
    where,
    getDocs
  } from 'firebase/firestore';
  import { db } from '../firebase';
  
  /* ---------- constants --------------------------------------------- */
  const PLUGIN_CODE_REGEX = /^(?=.*[A-Z])[A-Za-z0-9]{4}$/;
  
  /* ---------- schema ------------------------------------------------- */
  export const FIELD_SCHEMA = {
    object: {
      required: ['productType', 'name', 'pluginCode', 'productCode', 'internalProductCode']
    },
  
    instrument: {
      required: [
        'productType', 'name', 'pluginCode', 'productCode',
        'internalProductCode', 'needsActivationCode',
        'internalDownload', 'fileToDownload'
      ],
      optional: [
        'activationCodeSource', 'bucket', 'publicEncryptionKey',
        'privateEncryptionKey', 'generateKeys'
      ]
    },
  
    effect: {
      required: [
        'productType', 'name', 'pluginCode', 'productCode',
        'internalProductCode', 'needsActivationCode',
        'internalDownload', 'fileToDownload'
      ],
      optional: [
        'activationCodeSource', 'bucket', 'publicEncryptionKey',
        'privateEncryptionKey', 'generateKeys'
      ]
    },
  
    pack: {
      required: [
        'productType', 'name', 'pluginCode', 'productCode',
        'internalProductCode', 'internalDownload',
        'bucket', 'fileToDownload'
      ]
    },
  
    misc: {
      required: ['productType', 'name', 'pluginCode', 'productCode', 'internalProductCode']
    },
  
    bundle: {
      required: [
        'productType', 'name', 'pluginCode', 'productCode',
        'internalProductCode', 'bundleItems'
      ]
    }
  };
  
  /* ================================================================== */
  
  export async function addProductToFirestore(form) {
    const schema = FIELD_SCHEMA[form.productType];
    if (!schema) throw new Error('Invalid product type');
  
    /* ---------- pluginCode checks ---------------------------------- */
    if (!PLUGIN_CODE_REGEX.test(form.pluginCode || '')) {
      throw new Error(
        'pluginCode must be exactly 4 ASCII letters/digits with at least one uppercase letter.'
      );
    }
    const dupSnap = await getDocs(
      query(collection(db, 'products'), where('pluginCode', '==', form.pluginCode))
    );
    if (!dupSnap.empty) throw new Error(`pluginCode "${form.pluginCode}" already exists.`);
  
    /* ---------- IPC / bucket consistency --------------------------- */
    const expectedIPC = `${form.pluginCode}${form.productCode}`;
    if (form.internalProductCode !== expectedIPC) {
      throw new Error(
        `internalProductCode must equal pluginCode + productCode (“${expectedIPC}”).`
      );
    }
    if (form.internalDownload && form.bucket !== form.internalProductCode) {
      throw new Error(
        'bucket must equal internalProductCode when internalDownload is enabled.'
      );
    }
  
    /* ---------- required-field presence ---------------------------- */
    const payload = { createdAt: Timestamp.now() };
    const allFields = [...schema.required, ...(schema.optional || [])];
  
    for (const field of schema.required) {
      if (field === 'activationCodeSource' && !form.needsActivationCode) continue;
      if (field === 'bucket' && !form.internalDownload) continue;
      if (field === 'fileToDownload' && !form.internalDownload) continue;
  
      if (form[field] === '' || form[field] === undefined) {
        throw new Error(`Missing required field: ${field}`);
      }
    }
  
    /* ---------- key rules (respect generateKeys) ------------------- */
    if (
      form.needsActivationCode &&
      form.activationCodeSource === 'internal' &&
      !form.generateKeys
    ) {
      if (!form.publicEncryptionKey || !form.privateEncryptionKey) {
        throw new Error(
          'publicEncryptionKey and privateEncryptionKey are required unless “Generate Keys” is ticked.'
        );
      }
    }
  
    /* ---------- build payload -------------------------------------- */
    for (const field of allFields) {
      switch (field) {
        case 'bundleItems':
          if (form.bundleItems)
            payload.bundle_items = form.bundleItems
              .split(',')
              .map(x => x.trim())
              .filter(Boolean);
          break;
  
        case 'needsActivationCode':
        case 'internalDownload':
          payload[field] = !!form[field];
          break;
  
        case 'generateKeys':
          if (form.generateKeys) payload.generateKeys = true;
          break;
  
        case 'publicEncryptionKey':
        case 'privateEncryptionKey':
          if (!form.generateKeys && form[field]) payload[field] = form[field];
          break;
  
        default:
          if (form[field] !== '' && form[field] !== undefined) {
            payload[field] = form[field];
          }
      }
    }
  
    await addDoc(collection(db, 'products'), payload);
  }
  